/**
 * A máquina de estados conversacional no cliente.
 *
 * Duas responsabilidades, mantidas separadas de propósito (ADR-009):
 *
 * - `estado` (`ConversationState`) — o que o runtime está fazendo. Muda só por
 *   transição validada em `canTransition()`. Transição ilegal não é aplicada e
 *   vira aviso no console: é bug, não surpresa.
 * - `dome` (`DomeState`) — como o Will se apresenta. **Não** é derivada do
 *   estado por uma tabela: quem decide é a máquina de expressão de
 *   `@will/contracts`, com prioridade, duração, interrupção e fallback por
 *   regra. Este hook só a alimenta com eventos e com o relógio.
 *
 * O estado vive num ref além do useState porque os handlers do stream são
 * registrados uma vez e leriam um valor congelado se dependessem do closure de
 * render. O ref é a verdade; o useState existe para a interface redesenhar. A
 * expressão segue a mesma regra, pelo mesmo motivo — e porque ela também muda
 * sozinha, por tempo, sem evento nenhum chegando.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DOME,
  OCIOSO_APOS_MS,
  PRIORIDADE,
  REGRA_OCIOSO,
  TRANSITIONS,
  afetoDeStateChanged,
  aoEntrarEstado,
  aplicarEvento as aplicarEventoNaFace,
  aplicarRegra,
  avancar,
  canTransition,
  regraDeAfeto,
  resolver,
  type ConversationState,
  type DomeExpression,
  type DomeState,
  type ExpressaoAtiva,
  type U1Event,
} from '@will/contracts';
import { estaFalando, estadoDeStateChanged, rotaParaEvento } from './eventToState';
import { relatar } from '../client/telemetry';

export interface TransicaoLog {
  de: ConversationState;
  para: ConversationState;
  /** `false` quando a transição foi recusada por `canTransition()` */
  valida: boolean;
  motivo: string;
  em: number;
}

export interface UseConversation {
  /** estado conversacional corrente */
  estado: ConversationState;
  /** estado afetivo resolvido — o que o domo mostra agora */
  dome: DomeState;
  /** a expressão completa (rótulo, cor, saudação, legenda) do estado afetivo */
  expressao: DomeExpression;
  /** o Will está emitindo fala agora */
  falando: boolean;
  /** quem escolheu o rosto na tela: `'estado'` ou o gatilho que o instalou */
  origemDoRosto: string;
  /** há sobreposição instalada, calada pela trava de atividade */
  rostoTravado: boolean;
  /** últimas transições, para o painel de depuração */
  historico: readonly TransicaoLog[];

  /** Tenta uma transição. Devolve `false` (e avisa no console) se for ilegal. */
  transicionar: (para: ConversationState, motivo?: string) => boolean;
  /** Percorre uma rota, validando cada salto. Para no primeiro salto ilegal. */
  percorrer: (rota: readonly ConversationState[], motivo?: string) => boolean;
  /** Aplica um evento do stream: rota conversacional + expressão. */
  aplicarEvento: (evento: U1Event) => void;
  /** Sobrepõe a expressão do domo por um tempo. `ttlMs = 0` fixa até limpar. */
  expressar: (dome: DomeState, ttlMs?: number) => void;
  limparExpressao: () => void;
  /**
   * Escotilha de emergência: volta a `IDLE` sem validar.
   *
   * Necessária porque o contrato só alcança `IDLE` a partir de `ERROR`, e um
   * fim de sessão normal (`u1.session.ended`) não é erro. Usar só em ação
   * explícita — encerrar sessão ou recomeçar —, nunca no fluxo de eventos.
   */
  reiniciar: (motivo: string) => void;
}

const LIMITE_HISTORICO = 24;

export function useConversation(inicial: ConversationState = 'IDLE'): UseConversation {
  const estadoRef = useRef<ConversationState>(inicial);
  const [estado, setEstado] = useState<ConversationState>(inicial);
  const [historico, setHistorico] = useState<readonly TransicaoLog[]>([]);

  const faceRef = useRef<ExpressaoAtiva | null>(null);
  // Contador de redesenho em vez de guardar a expressão em estado: a face muda
  // por tempo, e um objeto novo a cada quadro faria o useMemo lá embaixo
  // recalcular à toa. O ref é a verdade; isto só avisa o React.
  const [revisao, setRevisao] = useState(0);
  const timerFace = useRef<number | null>(null);
  const timerOcioso = useRef<number | null>(null);

  const registrar = useCallback((log: TransicaoLog) => {
    setHistorico((anterior) => [...anterior, log].slice(-LIMITE_HISTORICO));
  }, []);

  /* ── O relógio da face ─────────────────────────────────────────────────── */

  // Declarado como ref porque `publicar` e `agendar` se chamam em ciclo: a
  // publicação agenda o próximo vencimento, e o vencimento publica.
  const agendarRef = useRef<() => void>(() => {});

  const publicar = useCallback((nova: ExpressaoAtiva | null): void => {
    if (nova === faceRef.current) return;
    faceRef.current = nova;
    agendarRef.current();
    setRevisao((n) => n + 1);
  }, []);

  const agendar = useCallback((): void => {
    if (timerFace.current !== null) {
      window.clearTimeout(timerFace.current);
      timerFace.current = null;
    }
    const atual = faceRef.current;
    if (atual === null || atual.expiraEm === null) return;
    // Mínimo de 16 ms: um vencimento já passado agendado com atraso 0 dispara
    // dentro do mesmo ciclo e, se `avancar` devolvesse a mesma referência,
    // giraria sem parar. Um quadro de folga corta essa possibilidade.
    const espera = Math.max(16, atual.expiraEm - Date.now());
    timerFace.current = window.setTimeout(() => {
      timerFace.current = null;
      publicar(avancar(faceRef.current, Date.now()));
    }, espera);
  }, [publicar]);

  agendarRef.current = agendar;

  /* ── Ociosidade ────────────────────────────────────────────────────────── */

  /**
   * Reinicia a contagem do cochilo.
   *
   * Chamado por qualquer sinal de vida — evento do stream, transição, ação do
   * usuário. Se nada acontecer por `OCIOSO_APOS_MS`, o Will cochila; e como a
   * regra é interrompível e de prioridade baixa, a primeira coisa que acontecer
   * o acorda.
   */
  const acordar = useCallback((): void => {
    if (timerOcioso.current !== null) window.clearTimeout(timerOcioso.current);
    const dormindo = faceRef.current?.prioridade === PRIORIDADE.OCIOSO;
    if (dormindo) publicar(null);
    timerOcioso.current = window.setTimeout(() => {
      timerOcioso.current = null;
      const agora = Date.now();
      publicar(aplicarRegra(avancar(faceRef.current, agora), REGRA_OCIOSO, agora, 'ocioso'));
    }, OCIOSO_APOS_MS);
  }, [publicar]);

  /* ── Transições ────────────────────────────────────────────────────────── */

  const transicionar = useCallback(
    (para: ConversationState, motivo = ''): boolean => {
      const de = estadoRef.current;
      if (de === para) return true;

      if (!canTransition(de, para)) {
        // Sugerir a escala legal encurta a depuração: quase sempre o autor da
        // transição esqueceu um passo intermediário do contrato.
        const pontes = TRANSITIONS[de].filter((meio) => canTransition(meio, para));
        console.warn(
          `[u1] transição inválida ${de} → ${para}${motivo ? ` (${motivo})` : ''}. ` +
            `Permitidas a partir de ${de}: ${TRANSITIONS[de].join(', ')}.` +
            (pontes.length > 0 ? ` Escala possível: ${pontes.join(' ou ')}.` : ''),
        );
        relatar('estado.transicao_invalida', { detalhe: `${de} -> ${para} (${motivo})` });
        registrar({ de, para, valida: false, motivo, em: Date.now() });
        return false;
      }

      estadoRef.current = para;
      setEstado(para);
      // A emoção de um turno não sobrevive ao turno seguinte: entrar em
      // USER_SPEAKING ou THINKING derruba sobreposições até `AFETO`.
      publicar(aoEntrarEstado(avancar(faceRef.current, Date.now()), para));
      acordar();
      registrar({ de, para, valida: true, motivo, em: Date.now() });
      return true;
    },
    [acordar, publicar, registrar],
  );

  const percorrer = useCallback(
    (rota: readonly ConversationState[], motivo = ''): boolean => {
      for (const passo of rota) {
        if (!transicionar(passo, motivo)) return false;
      }
      return true;
    },
    [transicionar],
  );

  /* ── Sobreposição manual ───────────────────────────────────────────────── */

  const limparExpressao = useCallback(() => {
    publicar(null);
  }, [publicar]);

  const expressar = useCallback(
    (dome: DomeState, ttlMs = 2_500) => {
      const agora = Date.now();
      publicar(
        aplicarRegra(
          avancar(faceRef.current, agora),
          {
            dome,
            prioridade: PRIORIDADE.REACAO,
            duracaoMs: ttlMs,
            interrompivel: true,
            fallback: null,
          },
          agora,
          'expressar()',
        ),
      );
      acordar();
    },
    [acordar, publicar],
  );

  const reiniciar = useCallback(
    (motivo: string) => {
      const de = estadoRef.current;
      if (de === 'IDLE') return;
      console.info(`[u1] máquina reiniciada a partir de ${de} (${motivo}).`);
      estadoRef.current = 'IDLE';
      setEstado('IDLE');
      publicar(null);
      registrar({ de, para: 'IDLE', valida: true, motivo: `reinício: ${motivo}`, em: Date.now() });
    },
    [publicar, registrar],
  );

  /* ── Eventos do stream ─────────────────────────────────────────────────── */

  const aplicarEvento = useCallback(
    (evento: U1Event) => {
      acordar();

      if (evento.type === 'u1.state.changed') {
        // O runtime é a autoridade sobre o próprio estado. Se o cliente
        // divergiu, sincronizar é menos danoso que ficar mostrando algo que
        // não está acontecendo — mas a divergência precisa aparecer, porque é
        // sintoma de mapeamento errado deste lado.
        const alvo = estadoDeStateChanged(evento.payload);
        if (alvo && alvo !== estadoRef.current) {
          const de = estadoRef.current;
          if (!transicionar(alvo, 'u1.state.changed')) {
            console.warn(
              `[u1] o runtime declarou ${alvo} vindo de ${de}, que o contrato não permite. ` +
                'Sincronizando à força — o mapa de eventos do cliente está fora de sincronia.',
            );
            estadoRef.current = alvo;
            setEstado(alvo);
            publicar(aoEntrarEstado(avancar(faceRef.current, Date.now()), alvo));
            registrar({ de, para: alvo, valida: false, motivo: 'sincronização forçada', em: Date.now() });
          }
        }

        // A emoção que o cérebro escolheu para o turno. Vem em toda transição,
        // mas só conta quando difere do chão daquele estado — ver
        // `afetoDeStateChanged`. Instalada DEPOIS da transição, senão o
        // `aoEntrarEstado` acima a derrubaria no mesmo instante.
        const afeto = afetoDeStateChanged(evento.payload);
        if (afeto !== null) {
          const agora = Date.now();
          publicar(
            aplicarRegra(avancar(faceRef.current, agora), regraDeAfeto(afeto), agora, 'cérebro'),
          );
        }
        return;
      }

      const rota = rotaParaEvento(estadoRef.current, evento.type);
      if (rota.length > 0) percorrer(rota, evento.type);

      const agora = Date.now();
      publicar(aplicarEventoNaFace(avancar(faceRef.current, agora), evento.type, agora));
    },
    [acordar, percorrer, publicar, registrar, transicionar],
  );

  // Começa a contar o cochilo já na montagem: uma aba aberta e esquecida antes
  // de qualquer evento também é ociosidade.
  useEffect(() => {
    acordar();
    return () => {
      if (timerFace.current !== null) window.clearTimeout(timerFace.current);
      if (timerOcioso.current !== null) window.clearTimeout(timerOcioso.current);
    };
  }, [acordar]);

  const face = resolver(estado, faceRef.current, Date.now());

  return useMemo(
    () => ({
      estado,
      dome: face.dome,
      expressao: DOME[face.dome],
      falando: estaFalando(estado),
      origemDoRosto: face.origem,
      rostoTravado: face.travada,
      historico,
      transicionar,
      percorrer,
      aplicarEvento,
      expressar,
      limparExpressao,
      reiniciar,
    }),
    // `revisao` entra de propósito: a face vive num ref, então é ele que avisa
    // que `resolver` acima devolveu outra coisa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      estado,
      face.dome,
      face.origem,
      face.travada,
      revisao,
      historico,
      transicionar,
      percorrer,
      aplicarEvento,
      expressar,
      limparExpressao,
      reiniciar,
    ],
  );
}
